import { useState, useEffect, useRef } from 'react';
import { obtenerMesas } from '../../services/mesasServicio';
import { sonarPedidoNuevo, sonarCuentaSolicitada } from '../../services/sonidosMesas';

/**
 * Add-on Pedidos QR: alertas de mesas para el menu del header.
 *
 * Vive en el menu, que se dibuja en casi todas las pantallas, asi que el
 * personal se entera de un pedido por QR o de una mesa pidiendo la cuenta sin
 * tener que estar parado en la pantalla de Mesas. Antes la alarma solo existia
 * ahi, y si el garzon estaba en Pedidos o en Inventario no se enteraba de nada.
 *
 * Es tambien el unico dueno de las alarmas sonoras: PedidosMesa ya no las toca,
 * si no sonarian dos veces cuando el garzon esta justo en esa pantalla.
 *
 * El backend manda tiene_pedido_nuevo y cuenta_solicitada apagados si el
 * negocio no tiene el add-on, asi que al resto no le cambia nada.
 */

const INTERVALO = 12000;

// Cada cuanto insiste cada alarma mientras nadie atiende. Mismos numeros que
// tenia la pantalla de Mesas: el pedido nuevo en cada ciclo (el umbral va bajo
// el periodo para que no se salte ciclos por el jitter del setInterval) y la
// cuenta cada 15s.
const REPETIR_PEDIDO_MS = 11000;
const REPETIR_CUENTA_MS = 15000;

const useAlertasMesas = (activo) => {
    const [pedidosNuevos, setPedidosNuevos] = useState(0);
    const [cuentasPedidas, setCuentasPedidas] = useState(0);

    const ultimoPedido = useRef(0);
    const ultimaCuenta = useRef(0);

    useEffect(() => {
        if (!activo) {
            setPedidosNuevos(0);
            setCuentasPedidas(0);
            return undefined;
        }

        let vivo = true;

        const revisar = async () => {
            let lista;
            try {
                const data = await obtenerMesas();
                lista = Array.isArray(data) ? data : [];
            } catch (e) {
                // Sin red no se apagan los contadores: si el garzon tenia una
                // mesa esperando, que siga marcada hasta saber lo contrario.
                return;
            }
            if (!vivo) return;

            const nuevos = lista.filter(m => m.tiene_pedido_nuevo).length;
            const cuentas = lista.filter(m => m.cuenta_solicitada).length;
            setPedidosNuevos(nuevos);
            setCuentasPedidas(cuentas);

            const ahora = Date.now();
            if (nuevos > 0 && ahora - ultimoPedido.current > REPETIR_PEDIDO_MS) {
                ultimoPedido.current = ahora;
                sonarPedidoNuevo();
            }
            if (cuentas > 0 && ahora - ultimaCuenta.current > REPETIR_CUENTA_MS) {
                ultimaCuenta.current = ahora;
                sonarCuentaSolicitada();
            }
        };

        revisar();
        const id = setInterval(revisar, INTERVALO);
        return () => { vivo = false; clearInterval(id); };
    }, [activo]);

    return { pedidosNuevos, cuentasPedidas, total: pedidosNuevos + cuentasPedidas };
};

export default useAlertasMesas;
